/**
 * The placement density report.
 *
 * Answers one question before any part is moved: how hard is this board going
 * to be to route, and where. Two numbers carry it — crossings, which says how
 * many layers the connections need in principle, and pressure, which says
 * which square inch of board is the problem.
 *
 * Ordered worst first throughout, and every row locates. A congestion figure
 * you cannot walk to on the board is a figure nobody acts on.
 */

import type { CongestionReport } from '@sircad/core/route/congestion.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface DensityReportOptions {
  report: CongestionReport;
  /** Render a length in the user's units. */
  formatLength(inches: number): string;
  /** Jump the view to a point and select what is there. */
  onLocate(x: number, y: number, entityIds?: number[]): void;
  /** Turn the overlay on or off. */
  onOverlay(on: boolean): void;
  overlayOn: boolean;
  /** Write the report out as text. */
  onSave(): void;
}

/** Rows shown per section before the rest are summarised. */
const PER_SECTION = 12;

export function showDensityReport(opts: DensityReportOptions): void {
  const r = opts.report;

  openDialog({
    title: 'Placement density',
    wide: true,
    build(body, handle) {
      const head = document.createElement('div');
      head.className = 'dfm-head';

      // The verdict is the layer count, because that is the decision the
      // report exists to inform.
      const enabled = r.routingLayers.length;
      const verdict = document.createElement('p');
      verdict.className = r.layersNeeded > enabled ? 'dfm-verdict bad'
        : r.overflow > 0 ? 'dfm-verdict warn' : 'dfm-verdict good';
      const layerWord = (n: number): string => `${n} layer${n === 1 ? '' : 's'}`;
      const cellWord = (n: number): string => `${n} cell${n === 1 ? '' : 's'}`;
      verdict.textContent = r.layersNeeded > enabled
        ? `These connections want ${layerWord(r.layersNeeded)}; ${layerWord(enabled)} `
          + `${enabled === 1 ? 'is' : 'are'} enabled.`
        : r.overflow > 0
          ? `It fits in ${layerWord(enabled)}, but ${cellWord(r.overCells)} `
            + `${r.overCells === 1 ? 'is' : 'are'} over capacity.`
          : `It fits comfortably in ${layerWord(enabled)}.`;
      head.appendChild(verdict);

      const stats = document.createElement('dl');
      stats.className = 'density-stats';
      const stat = (label: string, value: string, why?: string): void => {
        const dt = document.createElement('dt');
        dt.textContent = label;
        if (why) dt.title = why;
        const dd = document.createElement('dd');
        dd.textContent = value;
        stats.append(dt, dd);
      };

      stat('Crossings', String(r.crossings),
        'Connections that cross where they cannot both stay on one layer. '
        + 'None means the connections are planar and one layer would do.');
      stat('Peak pressure', r.peak.toFixed(2),
        'Tracks wanted against tracks that fit, in the busiest cell.');
      stat('Over capacity', cellWord(r.overCells));
      if (r.blockedCells > 0) {
        stat('Impassable', cellWord(r.blockedCells),
          'Traffic through cells nothing fits through at all.');
      }
      stat('Layers needed', String(r.layersNeeded));
      stat('Connection length', opts.formatLength(r.mstLength),
        'The shortest the copper could possibly be.');
      stat('Routing pitch', opts.formatLength(r.pitch));
      head.appendChild(stats);

      if (r.noOutline) {
        const warn = document.createElement('p');
        warn.className = 'dfm-note';
        warn.textContent = 'This board has no outline, so the edge clearance and '
          + 'the capacity figures are estimates over everything drawn.';
        head.appendChild(warn);
      }
      body.appendChild(head);

      // --- Sections ---------------------------------------------------------

      const section = (title: string, count: number): HTMLElement | null => {
        if (count === 0) return null;
        const wrap = document.createElement('section');
        wrap.className = 'dfm-group';
        const h = document.createElement('h3');
        h.textContent = `${title} — ${count}`;
        wrap.appendChild(h);
        body.appendChild(wrap);
        return wrap;
      };

      const row = (
        parent: HTMLElement, text: string, locate?: () => void,
      ): void => {
        const line = document.createElement('div');
        line.className = 'dfm-row';
        const span = document.createElement('span');
        span.textContent = text;
        line.appendChild(span);
        if (locate) {
          const go = document.createElement('button');
          go.type = 'button';
          go.className = 'link';
          go.textContent = 'Show';
          go.addEventListener('click', () => { locate(); handle.close(); });
          line.appendChild(go);
        }
        parent.appendChild(line);
      };

      const more = (parent: HTMLElement, n: number): void => {
        if (n <= 0) return;
        const p = document.createElement('p');
        p.className = 'dfm-note';
        p.textContent = `and ${n} more`;
        parent.appendChild(p);
      };

      // Busiest nets first: the ones sitting in the worst congestion.
      const busy = r.nets.filter((n) => !n.planed && n.worst > 0);
      const netSec = section('Nets in the worst congestion', busy.length);
      if (netSec) {
        for (const n of busy.slice(0, PER_SECTION)) {
          // Infinite pressure is not a big number, it is a different fact:
          // this net crosses somewhere nothing fits through at all.
          const where = Number.isFinite(n.worst)
            ? `pressure ${n.worst.toFixed(2)}` : 'crosses impassable board';
          row(netSec, `${n.net} — ${where}, `
            + `${n.crossings} crossing${n.crossings === 1 ? '' : 's'}, `
            + `${n.pins} pins, ${opts.formatLength(n.length)}`);
        }
        more(netSec, busy.length - PER_SECTION);
      }

      const overlapSec = section('Parts claiming the same area', r.overlaps.length);
      if (overlapSec) {
        for (const o of r.overlaps.slice(0, PER_SECTION)) {
          row(overlapSec, `${o.a} and ${o.b}`,
            () => opts.onLocate(o.at.x, o.at.y, o.ids));
        }
        more(overlapSec, r.overlaps.length - PER_SECTION);
      }

      const straddling = r.outside.filter((o) => !o.parked);
      const strSec = section('Straddling the board edge', straddling.length);
      if (strSec) {
        for (const o of straddling.slice(0, PER_SECTION)) row(strSec, o.ref);
        more(strSec, straddling.length - PER_SECTION);
      }

      const parked = r.outside.filter((o) => o.parked);
      const parkSec = section('Parked beside the board, not yet placed', parked.length);
      if (parkSec) {
        for (const o of parked.slice(0, PER_SECTION)) row(parkSec, o.ref);
        more(parkSec, parked.length - PER_SECTION);
      }

      const planed = r.nets.filter((n) => n.planed);
      if (planed.length > 0) {
        const p = document.createElement('p');
        p.className = 'dfm-note';
        p.textContent = `Poured on a plane, so not counted as demand: `
          + planed.map((n) => n.net).join(', ');
        body.appendChild(p);
      }

      // --- Buttons ----------------------------------------------------------

      const bar = buttons(body);
      const toggle = button(bar, opts.overlayOn ? 'Hide overlay' : 'Show on board', () => {
        opts.onOverlay(!opts.overlayOn);
        handle.close();
      });
      toggle.classList.add('primary');
      button(bar, 'Save report…', () => { opts.onSave(); });
      button(bar, 'Close', () => handle.close());
    },
  });
}
